// Variants that must never trigger the insurance auto-add.
// THE WAY OF THE RACER is a free-book offer advertised at a flat $8.95
// including shipping and handling, so a $3 add-on breaks the advertised price.
// A cart containing one of these plus a normal product still gets insurance.
const NO_INSURANCE_VARIANT_IDS = ["40066064318625"];

class CartCheckbox {
  constructor() {
    this.variantId = window.upsellVariantId;
    this.checkbox = document.querySelector(`#cart-checkbox-${this.variantId}`);
    this.isInitialized = false;
    this.isProcessing = false;
    this.optOutKey = 'pedalInsuranceOptOut';
    // Whether insurance was in the cart the last time we looked. A line that
    // disappears between syncs is how we spot a shopper-initiated removal.
    this.wasInCart = false;
    // Private browsing and blocked site data make localStorage throw, so keep
    // the opt-out in memory too. Without this the shopper's removal is
    // forgotten before the next resync and insurance comes straight back.
    this.optedOutMemory = false;

    if (this.checkbox && this.variantId) {
      console.log('Initializing with variant ID:', this.variantId);
      this.init();
    } else {
      console.error('Missing checkbox or variant ID');
    }
  }

  async init() {
    if (this.isInitialized) return;
    this.isInitialized = true;

    this.addEventListeners();
    this.bindCartEvents();
    await this.syncDefaultState();
  }

  // The shopper usually adds the first item AFTER this script runs, so re-evaluate
  // the default-on state on every cart change / drawer open, not just on page load.
  bindCartEvents() {
    const resync = () => this.syncDefaultState();
    document.addEventListener('ajaxProduct:added', resync); // item added to cart
    document.addEventListener('cart:updated', resync);      // quantity changed
    document.addEventListener('cart:build', resync);        // cart drawer rebuilt
    document.addEventListener('cart:open', resync);         // cart drawer opened
  }

  async syncDefaultState(cart) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      if (!cart) cart = await this.getCartState();
      const productInCart = this.isProductInCart(cart);
      const hasOtherItems = this.hasOtherItems(cart);

      // The line item's own Remove button and the quantity minus button drop
      // insurance from the cart without ever touching this checkbox. A line we
      // were tracking that is gone while real products remain is the shopper
      // saying no, so record the opt-out rather than adding it straight back.
      if (this.wasInCart && !productInCart && hasOtherItems) {
        this.setOptedOut(true);
      }

      if (!hasOtherItems) {
        // No real products in the cart: never leave an insurance-only cart, and
        // reset the opt-out so the next order starts opted-in again.
        this.setOptedOut(false);
        if (productInCart) await this.removeFromCart();
        this.checkbox.checked = false;
        this.wasInCart = false;
        return;
      }

      if (productInCart) {
        this.checkbox.checked = true;
        await this.ensureQuantityOne(cart);
        this.wasInCart = true;
      } else if (!this.hasOptedOut()) {
        // Default to opted-in: pre-add insurance unless the shopper explicitly removed it
        this.checkbox.checked = true;
        await this.addToCart(1);
        this.wasInCart = true;
      } else {
        this.checkbox.checked = false;
        this.wasInCart = false;
      }
    } catch (error) {
      console.error('[insurance] sync error:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  hasOptedOut() {
    if (this.optedOutMemory) return true;
    try {
      return localStorage.getItem(this.optOutKey) === '1';
    } catch (e) {
      return false;
    }
  }

  setOptedOut(value) {
    this.optedOutMemory = !!value;
    try {
      if (value) {
        localStorage.setItem(this.optOutKey, '1');
      } else {
        localStorage.removeItem(this.optOutKey);
      }
    } catch (e) {
      /* localStorage unavailable (private mode) — fall back to in-memory only */
    }
  }

  hasOtherItems(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some(item =>
      item.variant_id.toString() !== this.variantId.toString() &&
      NO_INSURANCE_VARIANT_IDS.indexOf(item.variant_id.toString()) === -1 &&
      item.quantity > 0
    );
  }

  addEventListeners() {
    this.checkbox.addEventListener('change', async (e) => {
      if (this.isProcessing) {
        e.preventDefault();
        return;
      }
      await this.handleCheckboxChange(e);
    });
  }

  async handleCheckboxChange(event) {
    const isChecked = event.target.checked;
    console.log('Checkbox state changed:', isChecked);

    this.isProcessing = true;
    this.checkbox.disabled = true; // Disable the checkbox
    this.showLoading(); // Show a loading indicator

    try {
      if (isChecked) {
        this.setOptedOut(false);
        const cartState = await this.getCartState();
        if (!this.isProductInCart(cartState)) {
          await this.addToCart(1);
        } else {
          await this.ensureQuantityOne(cartState);
        }
        this.wasInCart = true;
      } else {
        this.setOptedOut(true);
        await this.removeFromCart();
        this.wasInCart = false;
      }
    } catch (error) {
      console.error('Error handling checkbox change:', error);
      event.target.checked = !isChecked;
    } finally {
      this.isProcessing = false;
      this.checkbox.disabled = false; // Re-enable the checkbox
      this.hideLoading(); // Hide the loading indicator
    }
  }

  showLoading() {
    const loader = document.createElement('span');
    loader.className = 'loading-spinner'; // Add appropriate styles for this class
    this.checkbox.parentElement.appendChild(loader);
  }

  hideLoading() {
    const loader = this.checkbox.parentElement.querySelector('.loading-spinner');
    if (loader) {
      loader.remove();
    }
  }

  async addToCart(quantity) {
    try {
      console.log(`Adding variant ${this.variantId} to cart, quantity: ${quantity}`);

      const formData = {
        items: [{
          id: this.variantId,
          quantity: quantity
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Add to cart failed');

      const result = await response.json();
      console.log('Product added to cart:', result);

      // Immediately trigger cart drawer update
      await this.updateCartSection();
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  async updateCartSection() {
    const cartSection = document.querySelector('[data-section="cart"]');
    if (cartSection && window.Shopify?.sections?.reinit) {
      await window.Shopify.sections.reinit('cart');
      console.log('Cart section updated');
    } else {
      document.dispatchEvent(new CustomEvent('cart:build'));
    }
  }

  async removeFromCart() {
    try {
      console.log('Removing variant from cart:', this.variantId);

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      const itemToRemove = cart.items.find(item =>
        item.variant_id.toString() === this.variantId.toString()
      );

      if (!itemToRemove) {
        console.log('Item not found in cart');
        return;
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: itemToRemove.key,
          quantity: 0
        })
      });

      if (!response.ok) throw new Error('Remove from cart failed');

      const result = await response.json();
      console.log('Product removed from cart:', result);

      await this.updateCartSection();
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  }

  async getCartState() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  isProductInCart(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some(item =>
      item.variant_id.toString() === this.variantId.toString() && item.quantity > 0
    );
  }

  async ensureQuantityOne(cart) {
    const itemToUpdate = cart.items.find(item =>
      item.variant_id.toString() === this.variantId.toString()
    );

    if (itemToUpdate && itemToUpdate.quantity > 1) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: itemToUpdate.key,
            quantity: 1
          })
        });

        if (!response.ok) throw new Error('Failed to update cart quantity');

        const result = await response.json();
        console.log('Quantity adjusted:', result);
        await this.updateCartSection();
      } catch (error) {
        console.error('Error ensuring quantity is 1:', error);
      }
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new CartCheckbox();
});
